/* SJ-CAROUSEL-SMOOTH-V2-20261026 · Prepare nearby images before starting native animation. */
if (!customElements.get('sj-franchise-home')) {
  customElements.define('sj-franchise-home', class extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      const options = { signal: this.controller.signal };
      this.gallery = this.querySelector('[data-gallery]');
      if (!this.gallery) return;
      this.panel = this.querySelector('[data-panel]');
      this.openButton = this.querySelector('[data-open]');
      this.closeButton = this.querySelector('[data-close]');
      this.formHost = this.querySelector('[data-form-host]');
      this.reducedMotion = matchMedia('(prefers-reduced-motion: reduce)');
      this.speed = Math.max(10, Math.min(60, Number(this.dataset.speed) || 25));
      this.inViewport = this.hovered = this.focused = this.editingPhoto = false;
      this.inputMethod = 'keyboard';
      this.interactingUntil = 0;
      this.drag = null;
      this.dataset.carouselVersion = '20261026-v2';
      this.mediaReady = false;
      this.mediaGeneration = 0;
      this.warmStarted = false;
      this.decodedImages = new WeakMap();
      this.rows = Array.from(this.querySelectorAll('[data-viewport]')).map(viewport => ({
        viewport, original: viewport.querySelector('[data-original]'),
        track: viewport.querySelector('.sj-franchise__track'),
        direction: Number(viewport.dataset.direction) < 0 ? -1 : 1,
        loopWidth: 0, animation: null
      }));
      this.classList.add('is-ready');
      if (this.panel) {
        this.panel.inert = true;
        this.panel.setAttribute('aria-hidden', 'true');
        this.openButton.hidden = this.closeButton.hidden = false;
        this.openButton.addEventListener('click', () => this.setOpen(!this.classList.contains('is-open')), options);
        this.closeButton.addEventListener('click', () => this.setOpen(false), options);
        this.addEventListener('keydown', event => {
          if (event.key === 'Escape' && this.classList.contains('is-open')) this.setOpen(false);
        }, options);
      }
      this.gallery.addEventListener('pointerenter', event => {
        this.hovered = event.pointerType === 'mouse';
        this.updateMotion();
      }, options);
      this.gallery.addEventListener('pointerleave', () => {
        this.hovered = false;
        this.updateMotion();
      }, options);
      this.gallery.addEventListener('focusin', () => {
        this.focused = this.inputMethod === 'keyboard';
        this.updateMotion();
      }, options);
      this.gallery.addEventListener('focusout', event => {
        this.focused = this.inputMethod === 'keyboard' && this.gallery.contains(event.relatedTarget);
        this.updateMotion();
      }, options);
      document.addEventListener('keydown', event => {
        if (['Tab', 'ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) {
          this.inputMethod = 'keyboard';
          this.focused = this.gallery.contains(document.activeElement);
          this.updateMotion();
        }
      }, options);
      this.rows.forEach(row => {
        const viewport = row.viewport;
        viewport.addEventListener('dragstart', event => event.preventDefault(), options);
        viewport.addEventListener('pointerdown', event => {
          if (!row.animation || !event.isPrimary || event.button !== 0 || this.drag) return;
          this.inputMethod = 'pointer';
          this.focused = false;
          this.interactingUntil = Infinity;
          this.updateMotion();
          this.drag = { row, id: event.pointerId, type: event.pointerType,
            x: event.clientX, y: event.clientY, phase: this.phase(row), locked: false };
          if (event.pointerType === 'mouse') event.preventDefault();
        }, options);
        viewport.addEventListener('pointermove', event => {
          const drag = this.drag;
          if (!drag || drag.row !== row || drag.id !== event.pointerId) return;
          const dx = event.clientX - drag.x;
          const dy = event.clientY - drag.y;
          if (!drag.locked) {
            if (Math.max(Math.abs(dx), Math.abs(dy)) < 6) return;
            if (drag.type !== 'mouse' && Math.abs(dy) > Math.abs(dx)) {
              this.finishGesture();
              return;
            }
            drag.locked = true;
            viewport.setPointerCapture(event.pointerId);
            viewport.classList.add('is-dragging');
          }
          if (event.cancelable) event.preventDefault();
          this.seek(row, drag.phase - dx / row.loopWidth);
        }, options);
        viewport.addEventListener('lostpointercapture', () => this.finishGesture(), options);
        viewport.addEventListener('keydown', event => {
          if (!row.animation || !['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
          event.preventDefault();
          this.inputMethod = 'keyboard';
          this.focused = true;
          this.updateMotion();
          const distance = Math.max(100, viewport.clientWidth * 0.35) / row.loopWidth;
          this.seek(row, event.key === 'Home' ? 0 : event.key === 'End'
            ? Math.max(0, 1 - viewport.clientWidth / row.loopWidth)
            : this.phase(row) + (event.key === 'ArrowRight' ? distance : -distance));
        }, options);
        viewport.addEventListener('wheel', event => {
          // Consume horizontal trackpad movement only; vertical page scrolling remains native.
          if (!row.animation || Math.abs(event.deltaX) <= Math.abs(event.deltaY)) return;
          event.preventDefault();
          this.hold(1200);
          const unit = event.deltaMode === 1 ? 16 : event.deltaMode === 2 ? viewport.clientWidth : 1;
          this.seek(row, this.phase(row) + event.deltaX * unit / row.loopWidth);
        }, { ...options, passive: false });
      });
      const finish = event => {
        if (!this.drag || (event.pointerId !== undefined && event.pointerId !== this.drag.id)) return;
        this.finishGesture();
      };
      window.addEventListener('pointerup', finish, options);
      window.addEventListener('pointercancel', finish, options);
      window.addEventListener('blur', finish, options);
      document.addEventListener('visibilitychange', () => {
        if (document.hidden) this.finishGesture();
        this.updateMotion();
      }, options);
      this.reducedMotion.addEventListener('change', () => this.updateMotion(), options);
      this.addEventListener('shopify:block:select', event => {
        if (this.formHost?.contains(event.target)) { this.setOpen(true); return; }
        const photo = event.target.closest('.sj-franchise__photo');
        if (!photo) return;
        this.editingPhoto = true;
        this.setOpen(false);
        this.updateMotion();
        const row = this.rows.find(item => item.original.contains(photo));
        if (row?.animation) this.seek(row, (photo.offsetLeft - row.original.offsetLeft) / row.loopWidth);
      }, options);
      this.addEventListener('shopify:block:deselect', () => {
        this.editingPhoto = false;
        this.updateMotion();
      }, options);
      this.resizeObserver = new ResizeObserver(() => {
        cancelAnimationFrame(this.resizeFrame);
        this.resizeFrame = requestAnimationFrame(() => this.buildLoop());
      });
      this.rows.forEach(row => {
        this.resizeObserver.observe(row.viewport);
        this.resizeObserver.observe(row.original);
      });
      this.visibilityObserver = new IntersectionObserver(entries => {
        this.inViewport = entries[0].isIntersecting;
        this.updateMotion();
      });
      this.visibilityObserver.observe(this.gallery);
      this.buildLoop();
      // Load only this nearby gallery, not every image on the page at initial navigation.
      this.prepareObserver = new IntersectionObserver(entries => {
        if (!entries[0].isIntersecting) return;
        this.warmStarted = true;
        this.prepareObserver.disconnect();
        this.prepareImages();
      }, { rootMargin: '400px 0px' });
      this.prepareObserver.observe(this.gallery);
    }
    phase(row) {
      if (!row.animation || !row.duration) return row.direction === 1 ? 0 : 0.4;
      const progress = Number(row.animation.currentTime || 0) / row.duration;
      return this.wrap(row.direction === 1 ? progress : 1 - progress);
    }
    wrap(value) { return ((value % 1) + 1) % 1; }
    seek(row, phase) {
      if (!row.animation) return;
      const position = this.wrap(phase);
      row.animation.currentTime = (row.direction === 1 ? position : 1 - position) * row.duration;
    }
    buildLoop() {
      let changed = false;
      this.rows.forEach(row => {
        const width = row.viewport.clientWidth;
        // Computed width is fractional and is unaffected by theme entrance transforms.
        const sourceWidth = parseFloat(getComputedStyle(row.original).width);
        if (!row.original.children.length || !width || !sourceWidth) return;
        const copies = Math.ceil(width / sourceWidth);
        // Width-only changes do not restart a track when its period and required copies are unchanged.
        const signature = `${copies}:${sourceWidth}`;
        if (signature === row.signature) return;
        const phase = this.phase(row);
        if (this.drag?.row === row) this.finishGesture();
        row.signature = signature;
        changed = true;
        row.animation?.cancel();
        row.track.querySelectorAll('[data-loop-copy]').forEach(copy => copy.remove());
        row.loopWidth = sourceWidth;
        // One original period plus only enough copies to cover the viewport at the seam.
        for (let index = 0; index < copies; index += 1) {
          const copy = row.original.cloneNode(true);
          copy.removeAttribute('data-original');
          copy.setAttribute('data-loop-copy', '');
          copy.setAttribute('aria-hidden', 'true');
          copy.inert = true;
          [copy, ...copy.querySelectorAll('*')].forEach(element => {
            element.removeAttribute('id');
            element.removeAttribute('data-shopify-editor-block');
          });
          row.track.append(copy);
        }
        row.duration = sourceWidth / this.speed * 1000;
        const frames = [{ transform: 'translate3d(0,0,0)' }, { transform: `translate3d(${-sourceWidth}px,0,0)` }];
        if (row.direction === -1) frames.reverse();
        row.animation = row.track.animate(frames, { duration: row.duration, iterations: Infinity, easing: 'linear' });
        row.animation.pause();
        this.seek(row, phase);
        row.viewport.scrollLeft = 0; // Once at rebuild, never in the autoplay loop.
        row.viewport.classList.add('is-track-ready');
      });
      if (changed) {
        this.mediaGeneration += 1;
        this.mediaReady = false;
        if (this.warmStarted) this.prepareImages();
      }
      this.updateMotion();
    }
    decodeImage(image) {
      if (this.decodedImages.has(image)) return this.decodedImages.get(image);
      const signal = this.controller.signal;
      const task = new Promise(resolve => {
        let done = false;
        const finish = () => {
          if (done) return;
          done = true;
          clearTimeout(timeout);
          image.removeEventListener('load', loaded);
          image.removeEventListener('error', finish);
          signal.removeEventListener('abort', finish);
          resolve();
        };
        const loaded = () => {
          if (done) return;
          if (image.naturalWidth && image.decode) image.decode().then(finish, finish);
          else finish();
        };
        // A failed/very slow image must not permanently block manual browsing or autoplay.
        const timeout = setTimeout(finish, 5000);
        image.addEventListener('load', loaded, { once: true });
        image.addEventListener('error', finish, { once: true });
        signal.addEventListener('abort', finish, { once: true });
        image.decoding = 'async';
        image.loading = 'eager';
        if (image.complete) loaded();
      });
      this.decodedImages.set(image, task);
      return task;
    }
    prepareImages() {
      const generation = this.mediaGeneration;
      if (this.preparingGeneration === generation || !this.controller) return;
      this.preparingGeneration = generation;
      const controller = this.controller;
      const images = this.rows.flatMap(row => [...row.track.querySelectorAll('img')]);
      Promise.all(images.map(image => this.decodeImage(image))).then(() => {
        if (controller.signal.aborted || generation !== this.mediaGeneration) return;
        // Give the decoded, paused tracks a paint opportunity before both rows start.
        cancelAnimationFrame(this.warmFrame);
        this.warmFrame = requestAnimationFrame(() => {
          this.warmFrame = requestAnimationFrame(() => {
            if (controller.signal.aborted || generation !== this.mediaGeneration) return;
            this.mediaReady = true;
            this.updateMotion();
          });
        });
      });
    }
    hold(delay) {
      clearTimeout(this.gestureTimer);
      this.interactingUntil = performance.now() + delay;
      this.updateMotion();
      this.gestureTimer = setTimeout(() => this.updateMotion(), delay + 30);
    }
    finishGesture() {
      const drag = this.drag;
      if (!drag) return;
      this.drag = null;
      drag.row.viewport.classList.remove('is-dragging');
      if (drag.row.viewport.hasPointerCapture(drag.id)) drag.row.viewport.releasePointerCapture(drag.id);
      this.hold(drag.type === 'mouse' ? 0 : 2500);
    }
    updateMotion() {
      const stopped = !this.mediaReady || !this.hasAttribute('data-autoplay') || this.reducedMotion.matches || !this.inViewport
        || document.hidden || (this.hovered && this.hasAttribute('data-pausa-hover')) || this.focused
        || this.editingPhoto || this.classList.contains('is-open') || performance.now() < this.interactingUntil;
      this.rows.forEach(row => {
        if (!row.animation) return;
        if (stopped && row.animation.playState !== 'paused') row.animation.pause();
        else if (!stopped && row.animation.playState !== 'running') row.animation.play();
      });
    }
    setOpen(open) {
      if (!this.panel) return;
      this.finishGesture();
      this.classList.toggle('is-open', open);
      this.panel.inert = !open;
      this.panel.setAttribute('aria-hidden', String(!open));
      this.gallery.inert = open;
      this.gallery.setAttribute('aria-hidden', String(open));
      this.openButton.setAttribute('aria-expanded', String(open));
      this.updateMotion();
      const inner = this.querySelector('[data-panel-inner]');
      if (open && inner) inner.scrollTop = 0;
      (open ? this.closeButton : this.openButton).focus({ preventScroll: true });
    }
    disconnectedCallback() {
      this.controller?.abort();
      this.controller = null;
      cancelAnimationFrame(this.resizeFrame);
      cancelAnimationFrame(this.warmFrame);
      clearTimeout(this.gestureTimer);
      this.resizeObserver?.disconnect();
      this.visibilityObserver?.disconnect();
      this.prepareObserver?.disconnect();
      this.preparingGeneration = null;
      this.rows?.forEach(row => {
        row.animation?.cancel();
        row.viewport.classList.remove('is-track-ready', 'is-dragging');
      });
      this.querySelectorAll('[data-loop-copy]').forEach(copy => copy.remove());
      this.rows = [];
      this.drag = null;
      this.classList.remove('is-ready', 'is-open');
      if (this.gallery) { this.gallery.inert = false; this.gallery.removeAttribute('aria-hidden'); }
    }
  });
}
